import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icons as I } from '../pack-icons';
import { OPERATION_KINDS } from '../../state/GenerationStatusContext.jsx';
import GenerationFailureNotice from '../billing/GenerationFailureNotice.jsx';
import { platformDisplayName } from '../../lib/platforms.js';

/**
 * Per-operation row inside GenerationStatusPlate.
 *
 * Layout (CSS grid: ico | body | actions):
 *   - ico: rounded-square 28px badge tinted by status
 *   - body: title row (label + meta) + indeterminate/determinate progress
 *           + per-platform chips (when applicable) + inline error
 *   - actions: "View"/"Open" deeplink + dismiss × (× appears on hover)
 *
 * Status colors live in CSS — this component just hands them off via
 * .gen-row-{status}.
 */

const KIND_ICONS = {
  [OPERATION_KINDS.CONTENT_BATCH]: I.sparkles,
  [OPERATION_KINDS.SINGLE_DRAFT]: I.sparkles,
  [OPERATION_KINDS.PUBLISH_NOW]: I.send,
  [OPERATION_KINDS.PLAN_REGENERATE]: I.refresh,
  [OPERATION_KINDS.VISUAL]: I.image,
  [OPERATION_KINDS.BRANDKIT_IMAGE]: I.image,
  [OPERATION_KINDS.VIDEO_RENDER]: I.play || I.video || I.image,
  [OPERATION_KINDS.CAROUSEL]: I.copy,
  [OPERATION_KINDS.SOCIAL_CONNECT]: I.link,
  // Market-research run (driven by ResearchActivityBridge, not a batch).
  research: I.search,
};

function statusIcon(status) {
  if (status === 'succeeded') return I.check;
  if (status === 'failed') return I.alert;
  return null;
}

function ProgressBar({ progress, total, status }) {
  const { t } = useTranslation();
  if (status === 'succeeded' || status === 'failed') return null;
  const indeterminate = total == null;
  const pct = !indeterminate && total > 0
    ? Math.max(0, Math.min(100, Math.round((progress / total) * 100)))
    : null;
  const cls = `gen-prog ${indeterminate ? 'is-indeterminate' : ''}`;
  return (
    <div className={cls} aria-label={pct != null ? t('operationRow.percentComplete', { pct }) : t('operationRow.inProgress')}>
      <div className="gen-prog-fill" style={indeterminate ? undefined : { width: `${pct ?? 0}%` }} />
    </div>
  );
}

function PlatformChips({ platforms, platformStatus }) {
  if (!platforms?.length) return null;
  return (
    <div className="gen-plats">
      {platforms.map((p) => {
        const s = platformStatus?.[p] || 'running';
        const Icon = s === 'succeeded' ? I.check : s === 'failed' ? I.x : null;
        return (
          <span key={p} className={`gen-plat gen-plat-${s}`} title={`${platformDisplayName(p)}: ${s}`}>
            {Icon ? <Icon size={9} /> : <span className="gen-plat-dot" aria-hidden />}
            <span className="gen-plat-l">{platformDisplayName(p)}</span>
          </span>
        );
      })}
    </div>
  );
}

function statusMeta(op) {
  // What the right-hand meta column shows. We deliberately drop "Done"/
  // "Failed" text because the status-tinted background + green/red icon
  // already communicate the state — the extra word looked redundant and
  // visually noisy on a 380px-wide card. The fraction stays for batches
  // so the user can see "3/5 completed" at a glance.
  if (op.total != null && op.total > 1) {
    return `${op.completed ?? 0}/${op.total}`;
  }
  return '';
}

export default function OperationRow({ op, onDismiss }) {
  const { t } = useTranslation();
  const KindIcon = KIND_ICONS[op.kind] || I.sparkles;
  const StatusIcon = statusIcon(op.status);
  const Icon = StatusIcon || KindIcon;
  const isTerminal = op.status === 'succeeded' || op.status === 'failed' || op.status === 'cancelled';
  const isDone = op.status === 'succeeded';
  const isFailed = op.status === 'failed';
  const meta = statusMeta(op);

  return (
    <div className={`gen-row gen-row-${op.status}`} role="status">
      <span className="gen-row-ico" aria-hidden>
        <Icon size={14} />
      </span>

      <div className="gen-row-body">
        <div className="gen-row-head">
          <span className="gen-row-label">{op.label}</span>
          {meta && <span className="gen-row-meta">{meta}</span>}
        </div>

        <ProgressBar progress={op.progress ?? op.completed ?? 0} total={op.total} status={op.status} />

        {op.platforms?.length > 0 && (
          <PlatformChips platforms={op.platforms} platformStatus={op.platformStatus} />
        )}

        {isFailed && (op.error || op.errorCode) && (
          <div className="gen-row-error">
            <I.alert size={11} />
            <GenerationFailureNotice code={op.errorCode} message={op.error} />
          </div>
        )}
      </div>

      <div className="gen-row-actions">
        {op.externalUrl ? (
          // External target (e.g. the Telegram bot link) — open in a new tab so
          // the user can re-open it from Activity if they closed the tab. An
          // in-app <Link> would wrongly route inside the SPA.
          <a
            href={op.externalUrl}
            target="_blank"
            rel="noreferrer"
            className="gen-row-link"
            onClick={() => isTerminal && onDismiss?.(op.id)}
          >
            {t('operationRow.open')}
          </a>
        ) : op.deeplink ? (
          <Link
            to={op.deeplink}
            className="gen-row-link"
            onClick={() => isTerminal && onDismiss?.(op.id)}
          >
            {isDone ? t('operationRow.view') : t('operationRow.open')}
          </Link>
        ) : null}
        {/* Always dismissable — gives the user an escape hatch for an op that
            looks stuck (e.g. a regen whose synchronous request was orphaned by
            a reload). The × is hidden until row-hover via CSS, so it doesn't
            clutter a healthy running op. Dismissing only hides the indicator;
            any real server-side work keeps going and surfaces on its screen. */}
        <button
          type="button"
          className="gen-row-dismiss"
          onClick={() => onDismiss?.(op.id)}
          aria-label={t('operationRow.dismiss')}
          title={t('operationRow.dismiss')}
        >
          <I.x size={11} />
        </button>
      </div>
    </div>
  );
}
